import React, { useState, useCallback, useRef, useEffect } from 'react';
import VideoPlayer from '@client//components/VideoPlayer';
import ControlsOverlay from '@client/components/ControlsOverlay';
import HtmlContent from '@client/components/HtmlContent';
import AutoPlayOverlay from '@client/components/AutoPlayOverlay';
import CourseSidebar from '@client/components/CourseSidebar';
import { Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { CourseData } from '../types';

type LessonType = 'video' | 'content';

export const LessonPage: React.FC = () => {
    const { course } = useParams<{ course: string }>();
    const courseLessonIdStorageItem = `${course}_lesson_id`;
    const courseAutoplayStorageItem = `${course}_autoplay`;
    const [showControls, setShowControls] = useState(false);

    // Inicializar Autoplay desde LocalStorage
    const [isAutoplay, setIsAutoplay] = useState(() => {
        try {
            const saved = localStorage.getItem(courseAutoplayStorageItem);
            return saved !== null ? JSON.parse(saved) : true;
        } catch (e) {
            console.warn('Error reading autoplay from localStorage', e);
            return true;
        }
    });

    const [isFavorite, setIsFavorite] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Estado de carga y datos
    const [loading, setLoading] = useState(true);
    const [lessonType, setLessonType] = useState<LessonType>('video');
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [htmlContent, setHtmlContent] = useState<string>('');
    const [videoTitle, setVideoTitle] = useState('');

    // Estado para la cuenta atrás de Autoplay
    const [showAutoPlayOverlay, setShowAutoPlayOverlay] = useState(false);
    // Estado auxiliar para forzar la recarga del componente VideoPlayer al darle "Volver a reproducir"
    const [replayTrigger, setReplayTrigger] = useState(0);

    // Estados para el Contenido del Curso (Sidebar)
    const [courseData, setCourseData] = useState<CourseData | null>(null);
    const [showSidebar, setShowSidebar] = useState(false);

    // Inicializar LessonId desde LocalStorage (Default: 3)
    const [lessonId, setLessonId] = useState(() => {
        try {
            const saved = localStorage.getItem(courseLessonIdStorageItem);
            const parsed = parseInt(saved || '', 10);
            return isNaN(parsed) ? 1 : parsed;
        } catch (e) {
            console.warn(`Error reading ${courseLessonIdStorageItem} from localStorage`, e);
            return 1;
        }
    });

    const containerRef = useRef<HTMLDivElement>(null);

    // Efecto para guardar la preferencia de Autoplay cuando cambie
    useEffect(() => {
        localStorage.setItem(courseAutoplayStorageItem, JSON.stringify(isAutoplay));
    }, [isAutoplay]);

    // Efecto para guardar el progreso de la lección cuando cambie
    useEffect(() => {
        localStorage.setItem(courseLessonIdStorageItem, lessonId.toString());
    }, [lessonId]);

    // Cargar Estructura del Curso (Solo una vez al montar)
    useEffect(() => {
        const fetchCourseStructure = async () => {
            try {
                const response = await fetch(`/api/courses/${course}`);
                if (response.ok) {
                    const data = await response.json();
                    setCourseData(data);
                }
            } catch (error) {
                console.error('Error fetching course structure:', error);
            }
        };
        fetchCourseStructure();
    }, []);

    const fetchVideoData = useCallback(async () => {
        setLoading(true);
        setVideoUrl(null); // Resetear URL anterior
        setHtmlContent(''); // Resetear contenido anterior
        setShowAutoPlayOverlay(false); // Asegurar que el overlay se oculte al cargar nueva lección

        try {
            const width = window.innerWidth;
            const response = await fetch(`/api/courses/${course}/${lessonId}?innerWidth=${width}`);

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();

            if (data) {
                setVideoTitle(data.title);

                // Determinamos el tipo de contenido basado en la respuesta
                if (data.type === 'content') {
                    setLessonType('content');
                    setHtmlContent(data.content || '');
                } else {
                    // Asumimos video por defecto si hay propiedad video o no es type content
                    setLessonType('video');
                    setVideoUrl(data.video);
                }
            }
        } catch (error) {
            console.error('Error fetching course data:', error);
            setVideoUrl(null);
            setHtmlContent('');
        } finally {
            setLoading(false);
        }
    }, [lessonId]);

    useEffect(() => {
        fetchVideoData();
    }, [fetchVideoData]);

    const handleMouseMove = (e: React.MouseEvent) => {
        // Si hay un overlay activo, no queremos mostrar los controles
        if (showAutoPlayOverlay || showSidebar) {
            setShowControls(false);
            return;
        }

        // Solo mostramos controles si es tipo video
        if (lessonType !== 'video') return;

        if (e.clientY < window.innerHeight * 0.25) {
            setShowControls(true);
        } else {
            setShowControls(false);
        }
    };

    const handleVideoEnded = useCallback(() => {
        setShowAutoPlayOverlay(true);
        setShowControls(false);
    }, []);

    // Acciones del Overlay de Autoplay
    const handleAutoPlayComplete = () => {
        setShowAutoPlayOverlay(false);
        setLessonId((prev) => prev + 1);
    };

    const handleAutoPlayReplay = () => {
        setShowAutoPlayOverlay(false);
        setReplayTrigger((prev) => prev + 1);
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch((err) => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    useEffect(() => {
        const handleFsChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFsChange);
        return () => document.removeEventListener('fullscreenchange', handleFsChange);
    }, []);

    const handleNext = () => setLessonId((prev) => prev + 1);
    const handlePrev = () => setLessonId((prev) => Math.max(1, prev - 1));

    const handleSelectLesson = (index: number) => {
        setLessonId(index);
        setShowSidebar(false);
    };

    // Lógica para determinar si mostrar la barra superior
    const isOverlayVisible = !showAutoPlayOverlay && !showSidebar && (lessonType === 'content' || showControls);

    return (
        <div
            ref={containerRef}
            // Se usa h-[100dvh] para que en iOS Safari el alto se ajuste dinámicamente descontando las barras del navegador
            className="relative w-full h-[100dvh] bg-black overflow-hidden group select-none flex items-center justify-center"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setShowControls(false)}
        >
            {loading ? (
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-12 h-12 text-white/20 animate-spin" />
                    <p className="text-white/40 text-sm font-medium tracking-widest uppercase">
                        Cargando Lección {lessonId}...
                    </p>
                </div>
            ) : (
                <>
                    {/* Renderizado Condicional del Contenido Principal */}
                    {lessonType === 'video' && videoUrl && (
                        <VideoPlayer key={`${videoUrl}-${replayTrigger}`} url={videoUrl} onEnded={handleVideoEnded} />
                    )}

                    {lessonType === 'content' && htmlContent && (
                        <HtmlContent
                            content={htmlContent}
                            title={videoTitle}
                            onNext={handleNext}
                            onPrev={handlePrev}
                            hasPrev={lessonId > 1}
                        />
                    )}

                    {/* Sidebar de Contenido del Curso */}
                    <CourseSidebar
                        isOpen={showSidebar}
                        onClose={() => setShowSidebar(false)}
                        courseData={courseData}
                        currentLessonId={lessonId}
                        onSelectLesson={handleSelectLesson}
                    />

                    {/* Overlay de Cuenta Atrás (Autoplay) */}
                    {showAutoPlayOverlay && (
                        <AutoPlayOverlay
                            seconds={5}
                            isAutoplay={isAutoplay}
                            onCancel={handleAutoPlayReplay}
                            onConfirm={handleAutoPlayComplete}
                            onComplete={handleAutoPlayComplete}
                        />
                    )}

                    {/* Renderizado de la barra superior (Compartida) */}
                    {/* Mostramos la barra incluso si está cargando solo si tenemos datos previos, pero aquí simplificamos */}
                    {(videoUrl || htmlContent) && (
                        <ControlsOverlay
                            isVisible={isOverlayVisible}
                            title={videoTitle}
                            duration={`Lección ${lessonId} ${courseData ? `/ ${courseData.lessons}` : ''}`}
                            isAutoplay={isAutoplay}
                            setIsAutoplay={setIsAutoplay}
                            isFavorite={isFavorite}
                            setIsFavorite={setIsFavorite}
                            toggleFullscreen={toggleFullscreen}
                            isFullscreen={isFullscreen}
                            onNext={handleNext}
                            onPrev={handlePrev}
                            onToggleContent={() => setShowSidebar(true)}
                        />
                    )}

                    {/* Estado de error / vacío */}
                    {!videoUrl && !htmlContent && (
                        <div className="text-white/50 text-center relative z-10">
                            <p>No se pudo cargar la lección {lessonId}.</p>
                            <div className="flex gap-4 justify-center mt-4">
                                <button
                                    onClick={handlePrev}
                                    disabled={lessonId <= 1}
                                    className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-full text-sm transition-colors disabled:opacity-50"
                                >
                                    Anterior
                                </button>
                                <button
                                    onClick={fetchVideoData}
                                    className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-full text-sm transition-colors"
                                >
                                    Reintentar
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
